// Адмін: CRUD промокодів + аналітика партнерських продажів.
//
// Дві ролі промокоду:
//   1. Маркетингова акція (BLACKFRIDAY) — без партнера, commissionPercent = 0
//   2. Партнерський (BLOGER20) — з partnerName/Contact, commissionPercent ≥ 20
//
// Кожна картка показує агреговану статистику; "Деталі" розгортає список юзерів
// з повною інформацією про оплату. Кнопка "📥 CSV" вивантажує таблицю в Excel.
import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

type Promocode = NonNullable<Awaited<ReturnType<typeof api.admin.listPromocodes>>>[number];

export function AdminPromocodes() {
  const list = useFetch(() => api.admin.listPromocodes(), []);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'partners' | 'campaigns'>('all');

  const data = list.data;
  const filtered = data?.filter((p) => {
    if (filter === 'partners') return !!p.partnerName || p.commissionPercent > 0;
    if (filter === 'campaigns') return !p.partnerName && p.commissionPercent === 0;
    return true;
  });

  // Агрегати по всіх промокодах для топ-зведення
  const totalRevenue = data?.reduce((s, p) => s + p.totalRevenueUah, 0) ?? 0;
  const totalCommission = data?.reduce((s, p) => s + p.commissionUah, 0) ?? 0;
  const totalPaid = data?.reduce((s, p) => s + p.paidUsesCount, 0) ?? 0;
  const totalUses = data?.reduce((s, p) => s + p.actualUses, 0) ?? 0;

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 flex-1 text-xs text-tg-hint">
          Промокоди — знижка для юзера + комісія партнеру (якщо вказано). Один юзер = одне використання.
        </p>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="shrink-0 rounded-lg bg-tg-button px-3 py-1.5 text-xs font-semibold text-tg-button-text"
        >
          {showForm ? '✕ Закрити' : '+ Новий'}
        </button>
      </div>

      {/* Глобальна статистика */}
      {data && data.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatTile label="Усього використань" value={totalUses.toString()} />
          <StatTile
            label="Оплачено"
            value={totalPaid.toString()}
            sub={totalUses ? `${((totalPaid / totalUses) * 100).toFixed(1)}% конв.` : '0%'}
          />
          <StatTile label="Виручка" value={`${totalRevenue} ₴`} accent="success" />
          <StatTile label="До виплати партнерам" value={`${totalCommission} ₴`} accent="warning" />
        </div>
      )}

      {showForm && <PromocodeForm onSaved={() => { setShowForm(false); list.reload(); }} />}

      {/* Фільтр */}
      {data && data.length > 0 && (
        <div className="-mx-1 flex gap-1.5 overflow-x-auto pb-1">
          <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')}>
            Усі ({data.length})
          </FilterBtn>
          <FilterBtn active={filter === 'partners'} onClick={() => setFilter('partners')}>
            🤝 Партнерські ({data.filter((p) => !!p.partnerName || p.commissionPercent > 0).length})
          </FilterBtn>
          <FilterBtn active={filter === 'campaigns'} onClick={() => setFilter('campaigns')}>
            📢 Кампанії ({data.filter((p) => !p.partnerName && p.commissionPercent === 0).length})
          </FilterBtn>
        </div>
      )}

      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {data?.length === 0 && (
        <p className="card text-sm text-tg-hint">
          Промокодів ще немає. Створи перший вище. Для партнерів задавай <code>partnerName</code> + <code>commissionPercent</code> щоб трекати виплати.
        </p>
      )}

      {filtered?.map((p) => (
        <PromocodeCard key={p.id} p={p} onChanged={list.reload} />
      ))}
    </div>
  );
}

// ===== Subcomponents =====

function StatTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: 'success' | 'warning';
}) {
  const valueCls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'warning'
        ? 'text-yellow-500'
        : 'text-tg-text';
  return (
    <div className="rounded-lg bg-tg-secondary-bg p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-tg-hint">{label}</div>
      <div className={`mt-1 text-lg font-bold leading-none ${valueCls}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-tg-hint">{sub}</div>}
    </div>
  );
}

function FilterBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${
        active ? 'bg-tg-button text-tg-button-text' : 'bg-tg-secondary-bg text-tg-text'
      }`}
    >
      {children}
    </button>
  );
}

// Форма створення/редагування. Якщо передано initial — режим редагування.
function PromocodeForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Promocode;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const isEdit = !!initial;
  const [code, setCode] = useState(initial?.code ?? '');
  const [discount, setDiscount] = useState(initial?.discountPercent ?? 20);
  const [maxUses, setMaxUses] = useState<string>(initial?.maxUses?.toString() ?? '');
  const [validUntil, setValidUntil] = useState<string>(
    initial?.validUntil ? new Date(initial.validUntil).toISOString().slice(0, 10) : '',
  );
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [partnerName, setPartnerName] = useState(initial?.partnerName ?? '');
  const [partnerContact, setPartnerContact] = useState(initial?.partnerContact ?? '');
  const [commission, setCommission] = useState(initial?.commissionPercent ?? 0);
  const [partnerSlug, setPartnerSlug] = useState(initial?.partnerSlug ?? '');
  // Кастомні ціни для трафіку з цього посилання (порожньо = стандартна ціна тарифу)
  const [price1m, setPrice1m] = useState<string>(initial?.priceOverride1m?.toString() ?? '');
  const [price3m, setPrice3m] = useState<string>(initial?.priceOverride3m?.toString() ?? '');
  const [price6m, setPrice6m] = useState<string>(initial?.priceOverride6m?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        discountPercent: discount,
        maxUses: maxUses ? Number(maxUses) : null,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        comment: comment.trim() || null,
        partnerName: partnerName.trim() || null,
        partnerContact: partnerContact.trim() || null,
        commissionPercent: commission,
        partnerSlug: partnerSlug.trim() || null,
        priceOverride1m: price1m ? Number(price1m) : null,
        priceOverride3m: price3m ? Number(price3m) : null,
        priceOverride6m: price6m ? Number(price6m) : null,
      };
      if (isEdit) {
        await api.admin.updatePromocode(initial!.id, payload);
      } else {
        await api.admin.createPromocode({ ...payload, code: code.trim() });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Помилка збереження');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-3">
      <h3 className="text-sm font-semibold">{isEdit ? 'Редагувати промокод' : 'Новий промокод'}</h3>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-tg-hint">Код</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="OLEG20"
            disabled={isEdit}
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 font-mono text-sm uppercase disabled:opacity-60"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-tg-hint">Знижка %</label>
          <input
            type="number"
            min={1}
            max={100}
            value={discount}
            onChange={(e) => setDiscount(Number(e.target.value))}
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-tg-hint">Ліміт використань</label>
          <input
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="без ліміту"
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-tg-hint">Дійсний до</label>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-tg-hint">Коментар (для адміна)</label>
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="напр. кампанія FB / партнер ABC"
          className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
      </div>

      {/* Партнерська програма */}
      <div className="space-y-2 rounded-lg bg-tg-bg/40 p-3">
        <div className="text-xs font-semibold text-tg-hint">🤝 Партнерська програма (опційно)</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-tg-hint">Ім'я партнера</label>
            <input
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="Олег / @назва_каналу"
              className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-tg-hint">Комісія %</label>
            <input
              type="number"
              min={0}
              max={100}
              value={commission}
              onChange={(e) => setCommission(Number(e.target.value))}
              className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-tg-hint">Контакт для виплат</label>
          <input
            value={partnerContact}
            onChange={(e) => setPartnerContact(e.target.value)}
            placeholder="@username, email або картка"
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-tg-hint">
            Slug реф-посилання <span className="opacity-60">(опціонально)</span>
          </label>
          <input
            value={partnerSlug}
            onChange={(e) => setPartnerSlug(e.target.value)}
            placeholder="наприклад: oleg-channel"
            pattern="[A-Za-z0-9_-]+"
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 font-mono text-sm lowercase"
          />
          <p className="mt-1 text-[11px] text-tg-hint">
            Кастомний URL: <code>t.me/pdr_testy_bot?start=partner_<strong>{partnerSlug.trim().toLowerCase() || (initial?.code?.toLowerCase() ?? 'CODE').toLowerCase()}</strong></code>
            <br />
            Якщо порожнє — використовується сам код промокоду. Дозволені латиниця, цифри, "-" і "_".
          </p>
        </div>
        <p className="text-[11px] text-tg-hint">
          Якщо комісія = 0 — це звичайна маркетингова акція, без виплат.
        </p>
      </div>

      {/* Кастомні ціни для трафіку з посилання */}
      <div className="space-y-2 rounded-lg bg-tg-bg/40 p-3">
        <div className="text-xs font-semibold text-tg-hint">💰 Ціни для цього посилання (опційно)</div>
        <p className="text-[11px] text-tg-hint">
          Хто зайде через <code>partner_{partnerSlug.trim().toLowerCase() || (code.trim().toLowerCase() || 'slug')}</code> —
          АВТОМАТИЧНО побачить і заплатить ці ціни (без вводу коду). Порожньо = стандарт (99 / 199 / 349 ₴).
        </p>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[11px] text-tg-hint">1 міс, ₴</label>
            <input
              type="number"
              min={1}
              value={price1m}
              onChange={(e) => setPrice1m(e.target.value)}
              placeholder="99"
              className="mt-1 w-full rounded-lg bg-tg-bg px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-tg-hint">3 міс, ₴</label>
            <input
              type="number"
              min={1}
              value={price3m}
              onChange={(e) => setPrice3m(e.target.value)}
              placeholder="199"
              className="mt-1 w-full rounded-lg bg-tg-bg px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-tg-hint">6 міс, ₴</label>
            <input
              type="number"
              min={1}
              value={price6m}
              onChange={(e) => setPrice6m(e.target.value)}
              placeholder="349"
              className="mt-1 w-full rounded-lg bg-tg-bg px-2 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {err && <div className="text-xs text-tg-destructive">{err}</div>}

      <div className="flex gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm"
          >
            Скасувати
          </button>
        )}
        <button type="submit" disabled={busy} className="btn-primary flex-1">
          {busy ? 'Збереження…' : isEdit ? 'Зберегти' : 'Створити'}
        </button>
      </div>
    </form>
  );
}

// ===== Картка промокоду — згорнута / розгорнута =====
function PromocodeCard({ p, onChanged }: { p: Promocode; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const expired = p.validUntil && new Date(p.validUntil) < new Date();
  const limitReached = p.maxUses !== null && p.usedCount >= p.maxUses;
  const usable = p.isActive && !expired && !limitReached;
  const isPartner = !!p.partnerName || p.commissionPercent > 0;

  async function toggleActive() {
    if (busy) return;
    setBusy(true);
    try {
      await api.admin.updatePromocode(p.id, { isActive: !p.isActive });
      onChanged();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  // Записати виплату партнеру: сума → (опційно) нотатка → «до виплати» зменшується
  async function recordPayout() {
    if (busy) return;
    const raw = prompt(
      `Скільки виплатив партнеру ${p.partnerName ?? p.code}?\n` +
        `Нараховано: ${p.totalCommissionUah} ₴ · вже виплачено: ${p.paidOutUah} ₴ · залишок: ${p.toPayUah} ₴\n\n` +
        'Сума виплати, грн:',
    );
    if (!raw) return;
    const amount = Number(raw.replace(',', '.').trim());
    if (!Number.isFinite(amount) || amount <= 0 || !Number.isInteger(amount)) {
      alert('Введи цілу суму в гривнях, наприклад 600');
      return;
    }
    const note = prompt('Нотатка (необовʼязково, напр. "на картку"):') ?? undefined;
    setBusy(true);
    try {
      await api.admin.addPartnerPayout(p.id, amount, note?.trim() || undefined);
      onChanged();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    if (!confirm(`Видалити промокод ${p.code}? Усі використання будуть видалені.`)) return;
    setBusy(true);
    try {
      await api.admin.deletePromocode(p.id);
      onChanged();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <PromocodeForm
        initial={p}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className={`card space-y-2 ${!usable ? 'opacity-60' : ''}`}>
      {/* Шапка */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-tg-bg px-2 py-0.5 font-mono font-bold text-tg-link">
              {p.code}
            </code>
            <span className="rounded bg-tg-success/15 px-2 py-0.5 text-xs font-semibold text-tg-success">
              −{p.discountPercent}%
            </span>
            {isPartner && (
              <span className="rounded bg-yellow-500/15 px-2 py-0.5 text-xs font-semibold text-yellow-500">
                🤝 {p.commissionPercent}% комісія
              </span>
            )}
            {!p.isActive && <span className="text-xs text-tg-hint">(неактивний)</span>}
            {expired && <span className="text-xs text-tg-destructive">(прострочено)</span>}
            {limitReached && <span className="text-xs text-tg-destructive">(ліміт)</span>}
          </div>
          {p.partnerName && (
            <div className="mt-1.5 text-xs text-tg-text">
              <span className="text-tg-hint">Партнер:</span> <strong>{p.partnerName}</strong>
              {p.partnerContact && <span className="text-tg-hint"> · {p.partnerContact}</span>}
            </div>
          )}
          {p.comment && <div className="mt-1 text-xs text-tg-hint">{p.comment}</div>}
        </div>
      </div>

      {/* Партнерський дашборд — посилання + кнопка копіювання */}
      {isPartner && p.partnerToken && (
        <PartnerLink
          token={p.partnerToken}
          code={p.code}
          slug={p.partnerSlug}
          promocodeId={p.id}
          onChanged={onChanged}
        />
      )}

      {/* Виплати партнеру: нараховано / виплачено / до виплати + кнопка запису */}
      {isPartner && (
        <div className="rounded-lg bg-gradient-to-br from-yellow-500/15 via-yellow-500/5 to-transparent border border-yellow-500/30 p-3">
          <div className="flex items-baseline justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-yellow-500 font-semibold">
                💰 До виплати ({p.commissionPercent}%)
              </div>
              <div className={`mt-0.5 text-2xl font-bold ${p.toPayUah > 0 ? 'text-yellow-500' : 'text-tg-success'}`}>
                {p.toPayUah} ₴
              </div>
            </div>
            <div className="text-right text-[11px] text-tg-hint">
              <div>нараховано: {p.totalCommissionUah} ₴</div>
              <div>виплачено: {p.paidOutUah} ₴</div>
              <div className="text-[10px]">(код {p.commissionUah} ₴ · посилання {p.acquiredCommissionUah} ₴)</div>
            </div>
          </div>
          <button
            onClick={recordPayout}
            disabled={busy}
            className="mt-2 w-full rounded-lg bg-yellow-500/20 px-3 py-1.5 text-xs font-semibold text-yellow-500 hover:bg-yellow-500/30 disabled:opacity-50"
          >
            💸 Записати виплату
          </button>
        </div>
      )}

      {/* Статистика — два рядки: промокод-канал + acquisition-канал */}
      <div className="space-y-2">
        {/* Промокод-канал */}
        <div>
          <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-tg-hint">
            <span>💳</span>
            <span>Через промокод</span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <MiniStat label="Введено код" value={p.actualUses.toString()} />
            <MiniStat
              label="Оплачено"
              value={p.paidUsesCount.toString()}
              accent={p.paidUsesCount > 0 ? 'success' : undefined}
              sub={p.actualUses > 0 ? `${p.conversionRate.toFixed(1)}% конв.` : undefined}
            />
            <MiniStat
              label="Виручка"
              value={`${p.totalRevenueUah} ₴`}
              accent={p.totalRevenueUah > 0 ? 'success' : undefined}
            />
            {isPartner ? (
              <MiniStat
                label="Комісія з коду"
                value={`${p.commissionUah} ₴`}
                accent="warning"
              />
            ) : (
              <MiniStat
                label="Знижок видано"
                value={`${p.totalSavedUah} ₴`}
                sub={p.lastUseAt ? new Date(p.lastUseAt).toLocaleDateString('uk-UA') : '—'}
              />
            )}
          </div>
        </div>

        {/* Acquisition-канал — тільки для партнерських */}
        {isPartner && (
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-tg-hint">
              <span>🔗</span>
              <span>Через посилання (без коду)</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <MiniStat
                label="Оплачено"
                value={p.acquiredPaidCount.toString()}
                accent={p.acquiredPaidCount > 0 ? 'success' : undefined}
              />
              <MiniStat
                label="Виручка"
                value={`${p.acquiredRevenueUah} ₴`}
                accent={p.acquiredRevenueUah > 0 ? 'success' : undefined}
              />
              <MiniStat
                label="Комісія"
                value={`${p.acquiredCommissionUah} ₴`}
                accent="warning"
              />
            </div>
          </div>
        )}
      </div>

      {/* Дрібні факти */}
      <div className="flex flex-wrap gap-3 text-[11px] text-tg-hint">
        {p.maxUses !== null && (
          <span>
            Ліміт: {p.usedCount}/{p.maxUses}
          </span>
        )}
        {p.validUntil && <span>До {new Date(p.validUntil).toLocaleDateString('uk-UA')}</span>}
        <span>Створено {new Date(p.createdAt).toLocaleDateString('uk-UA')}</span>
        {p.lastUseAt && (
          <span>Остання дія: {new Date(p.lastUseAt).toLocaleDateString('uk-UA')}</span>
        )}
      </div>

      {/* Кнопки */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={() => setExpanded((v) => !v)}
          disabled={p.actualUses === 0}
          className="rounded-lg bg-tg-button/15 px-3 py-1.5 text-xs font-semibold text-tg-link disabled:opacity-50"
        >
          {expanded ? '▲ Сховати деталі' : '▼ Деталі / користувачі'}
        </button>
        <button
          onClick={() => setEditing(true)}
          disabled={busy}
          className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
        >
          ✏️ Редагувати
        </button>
        <button
          onClick={toggleActive}
          disabled={busy}
          className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
        >
          {p.isActive ? '⏸ Деактивувати' : '▶ Активувати'}
        </button>
        <button
          onClick={remove}
          disabled={busy}
          className="rounded-lg bg-tg-destructive/15 px-3 py-1.5 text-xs text-tg-destructive"
        >
          🗑 Видалити
        </button>
      </div>

      {expanded && <DetailsView promocodeId={p.id} code={p.code} />}
    </div>
  );
}

// Партнерське посилання — публічний дашборд для партнера. Read-only,
// без логіну, з токеном у URL. Адмін копіює і кидає партнеру в Telegram.
function PartnerLink({
  token,
  code,
  slug,
  promocodeId,
  onChanged,
}: {
  token: string;
  code: string;
  slug: string | null;
  promocodeId: number;
  onChanged: () => void;
}) {
  const [copied, setCopied] = useState<'dashboard' | 'share' | null>(null);
  const [busy, setBusy] = useState(false);
  const dashboardUrl = `${window.location.origin}/partner/${token}`;
  // Telegram username читаємо з env-зминої webapp; fallback на дефолт
  const botUsername = (import.meta.env.VITE_BOT_USERNAME as string) || 'pdr_testy_bot';
  // Якщо є slug — використовуємо його, інакше код. Slug робить URL красивішим.
  const linkParam = slug || code;
  const shareUrl = `https://t.me/${botUsername}?start=partner_${encodeURIComponent(linkParam)}`;

  function copy(value: string, kind: 'dashboard' | 'share') {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    });
  }

  async function regenerate() {
    if (busy) return;
    if (
      !confirm(
        'Згенерувати новий токен? Старе посилання перестане працювати — треба буде надіслати партнеру нове.',
      )
    )
      return;
    setBusy(true);
    try {
      await api.admin.regeneratePromocodeToken(promocodeId);
      onChanged();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      {/* Реф-посилання для просування — кидати партнерові щоб він давав цю URL аудиторії */}
      <div className="rounded-lg border border-tg-success/30 bg-tg-success/5 p-3">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-tg-success">
          <span>🔗</span>
          <span>Реф-посилання для аудиторії партнера</span>
        </div>
        <div className="break-all rounded-lg bg-tg-bg/60 p-2 font-mono text-[11px] text-tg-text">
          {shareUrl}
        </div>
        <p className="mt-1.5 text-[11px] text-tg-hint">
          Юзер отримує 1 день Premium бонус. Ти бачиш скільки людей зайшло, активних, заблокованих.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={() => copy(shareUrl, 'share')}
            className="flex-1 rounded-lg bg-tg-success px-3 py-1.5 text-xs font-semibold text-white"
          >
            {copied === 'share' ? '✓ Скопійовано' : '📋 Скопіювати'}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs font-semibold text-tg-text"
          >
            🚀 Відкрити
          </a>
        </div>
      </div>

      {/* Дашборд для самого партнера — read-only сторінка зі статистикою */}
      <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-yellow-500">
          <span>📊</span>
          <span>Посилання на дашборд партнера</span>
        </div>
        <div className="break-all rounded-lg bg-tg-bg/60 p-2 font-mono text-[11px] text-tg-text">
          {dashboardUrl}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={() => copy(dashboardUrl, 'dashboard')}
            className="flex-1 rounded-lg bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-black"
          >
            {copied === 'dashboard' ? '✓ Скопійовано' : '📋 Скопіювати'}
          </button>
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs font-semibold text-tg-text"
          >
            👁 Відкрити
          </a>
          <button
            onClick={regenerate}
            disabled={busy}
            className="rounded-lg bg-tg-destructive/15 px-3 py-1.5 text-xs font-semibold text-tg-destructive"
          >
            🔁 Новий токен
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: 'success' | 'warning';
}) {
  const valueCls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'warning'
        ? 'text-yellow-500'
        : 'text-tg-text';
  return (
    <div className="rounded-lg bg-tg-bg/60 p-2">
      <div className="text-[10px] uppercase tracking-wide text-tg-hint">{label}</div>
      <div className={`mt-0.5 text-base font-bold leading-tight ${valueCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-tg-hint">{sub}</div>}
    </div>
  );
}

// ===== Деталі: список юзерів які скористались промокодом =====
function DetailsView({ promocodeId, code }: { promocodeId: number; code: string }) {
  const { data, loading, error, reload } = useFetch(
    () => api.admin.promocodeDetails(promocodeId),
    [promocodeId],
  );
  const [downloading, setDownloading] = useState(false);

  async function downloadCsv() {
    if (downloading) return;
    setDownloading(true);
    try {
      await api.admin.downloadPromocodeCsv(promocodeId, code);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не вдалося завантажити CSV');
    } finally {
      setDownloading(false);
    }
  }

  if (loading) return <div className="rounded-lg bg-tg-bg/40 p-3"><Loader /></div>;
  if (error) return <ErrorView error={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-3 rounded-lg bg-tg-bg/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Деталі використань · {data.rows.length}</h4>
        <button
          onClick={downloadCsv}
          disabled={downloading || data.rows.length === 0}
          className="rounded-lg bg-tg-success/15 px-3 py-1.5 text-xs font-semibold text-tg-success disabled:opacity-50"
        >
          {downloading ? '⏳ Готую…' : '📥 Скачати CSV'}
        </button>
      </div>

      {data.rows.length === 0 ? (
        <p className="text-xs text-tg-hint">Поки що ніхто не скористався цим промокодом.</p>
      ) : (
        <div className="space-y-2">
          {data.rows.map((row) => (
            <UserRow key={row.useId} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserRow({ row }: { row: NonNullable<Awaited<ReturnType<typeof api.admin.promocodeDetails>>>['rows'][number] }) {
  const purchaseStatus = row.purchase?.status;
  const isPaid = purchaseStatus === 'APPROVED';
  const statusCls = isPaid
    ? 'text-tg-success'
    : purchaseStatus === 'PENDING'
      ? 'text-yellow-500'
      : 'text-tg-hint';
  const statusText = isPaid
    ? '✓ оплачено'
    : purchaseStatus === 'PENDING'
      ? '⏳ очікує'
      : purchaseStatus === 'DECLINED'
        ? '✕ відхилено'
        : purchaseStatus === 'REFUNDED'
          ? '↩ повернуто'
          : '— не оплачено';

  const name = [row.user.firstName, row.user.lastName].filter(Boolean).join(' ').trim() || 'Без імені';

  return (
    <div className="rounded-lg bg-tg-secondary-bg p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium">{name}</span>
            {row.user.username && (
              <span className="text-xs text-tg-hint">@{row.user.username}</span>
            )}
            {row.user.isPremium && (
              <span className="rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-500">
                👑 Premium
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-tg-hint">
            tg:{row.user.telegramId ?? '—'} · застосував {new Date(row.usedAt).toLocaleString('uk-UA')}
          </div>
        </div>
        <div className={`shrink-0 text-right text-xs font-semibold ${statusCls}`}>
          {statusText}
        </div>
      </div>

      {/* Дані оплати */}
      {row.purchase && (
        <div className="mt-2 grid grid-cols-3 gap-2 border-t border-tg-bg/40 pt-2 text-[11px]">
          <div>
            <div className="text-tg-hint">Сума</div>
            <div className="font-semibold text-tg-text">{row.purchase.amountUah} ₴</div>
          </div>
          <div>
            <div className="text-tg-hint">Підписка</div>
            <div className="font-semibold text-tg-text">{row.purchase.months} міс</div>
          </div>
          <div>
            <div className="text-tg-hint">Знижка</div>
            <div className="font-semibold text-tg-success">{row.amountSavedUah} ₴</div>
          </div>
          {row.purchase.approvedAt && (
            <div className="col-span-3 text-[10px] text-tg-hint">
              Оплачено: {new Date(row.purchase.approvedAt).toLocaleString('uk-UA')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
